// src/components/ApiKeys/ApiKeys.tsx
import React, { useState, useEffect } from 'react';
import { consumerService } from '../../services/consumerService';
import { apiKeyService } from '../../services/apiKeyService';
import { Consumer, ApiKey } from '../../types';
import { Key, Calendar, Clock, Shield } from 'lucide-react';
import './ApiKeys.css';

interface ApiKeyWithConsumer extends ApiKey {
  consumerId?: number;
}

const ApiKeys: React.FC = () => {
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [allApiKeys, setAllApiKeys] = useState<ApiKeyWithConsumer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsumerId, setSelectedConsumerId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const consumersResponse = await consumerService.getAll();
      setConsumers(consumersResponse.data);

      // Fetch all API keys for all consumers
      const allKeys: ApiKeyWithConsumer[] = [];
      for (const consumer of consumersResponse.data) {
        try {
          const keysResponse = await apiKeyService.getByConsumer(consumer.id);
          const keysWithConsumerId = keysResponse.data.map(key => ({
            ...key,
            consumerId: consumer.id
          }));
          allKeys.push(...keysWithConsumerId);
        } catch (error) {
          console.error(`Error fetching keys for consumer ${consumer.id}:`, error);
        }
      }
      setAllApiKeys(allKeys);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConsumerName = (consumerId?: number): string => {
    if (!consumerId) return 'Unknown';
    const consumer = consumers.find(c => c.id === consumerId);
    return consumer?.companyName || 'Unknown';
  };

  const filteredKeys = selectedConsumerId
    ? allApiKeys.filter(key => key.consumerId === selectedConsumerId)
    : allApiKeys;

  if (loading) return <div className="loading">Loading API keys...</div>;

  return (
    <div className="api-keys">
      <div className="page-header">
        <h2 className="page-title">API Keys Overview</h2>
        <select
          className="form-input"
          value={selectedConsumerId || ''}
          onChange={(e) => setSelectedConsumerId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">All Consumers</option>
          {consumers.map(consumer => (
            <option key={consumer.id} value={consumer.id}>
              {consumer.companyName}
            </option>
          ))}
        </select>
      </div>

      <div className="keys-stats">
        <div className="stat-mini">
          <Key size={20} />
          <div>
            <p className="stat-mini-value">{allApiKeys.length}</p>
            <p className="stat-mini-label">Total Keys</p>
          </div>
        </div>
        <div className="stat-mini">
          <Shield size={20} />
          <div>
            <p className="stat-mini-value">{allApiKeys.filter(k => k.isActive).length}</p>
            <p className="stat-mini-label">Active Keys</p>
          </div>
        </div>
        <div className="stat-mini">
          <Calendar size={20} />
          <div>
            <p className="stat-mini-value">
              {allApiKeys.filter(k => k.expiryDate && new Date(k.expiryDate) < new Date()).length}
            </p>
            <p className="stat-mini-label">Expired Keys</p>
          </div>
        </div>
        <div className="stat-mini">
          <Clock size={20} />
          <div>
            <p className="stat-mini-value">
              {allApiKeys.filter(k => {
                if (!k.lastUsedDate) return false;
                const daysSinceUse = (Date.now() - new Date(k.lastUsedDate).getTime()) / (1000 * 60 * 60 * 24);
                return daysSinceUse <= 7;
              }).length}
            </p>
            <p className="stat-mini-label">Used This Week</p>
          </div>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Key Prefix</th>
              <th>Friendly Name</th>
              <th>Consumer</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Last Used</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredKeys.map(key => (
              <tr key={key.id}>
                <td className="font-mono">{key.keyPrefix}...</td>
                <td>{key.friendlyName || '-'}</td>
                <td>{getConsumerName(key.consumerId)}</td>
                <td>{new Date(key.createdDate).toLocaleDateString()}</td>
                <td>
                  {key.expiryDate ? (
                    <span className={new Date(key.expiryDate) < new Date() ? 'text-danger' : ''}>
                      {new Date(key.expiryDate).toLocaleDateString()}
                    </span>
                  ) : (
                    'Never'
                  )}
                </td>
                <td>{key.lastUsedDate ? new Date(key.lastUsedDate).toLocaleDateString() : 'Never'}</td>
                <td>
                  <span className={`status-badge ${key.isActive ? 'status-active' : 'status-inactive'}`}>
                    {key.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredKeys.length === 0 && (
          <div className="empty-state">
            <Key className="empty-state-icon" size={48} />
            <p className="empty-state-title">No API Keys Found</p>
            <p className="empty-state-description">
              {selectedConsumerId
                ? 'This consumer has no API keys yet.'
                : 'No API keys have been generated yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeys;
